import React, { useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress, Alert, Button } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import AnalysisContent from "../../components/AnalysisContent/AnalysisContent";
import {
  ShareDialog,
  ShareDialogHandle,
} from "../../components/ShareDialog/ShareDialog";
import {
  SharedToLinkedInToast,
  SharedToLinkedInToastHandle,
} from "../../components/SharedToLinkedInToast/SharedToLinkedInToast";
import { useServices } from "../../services/Services";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import type { Osdk } from "@osdk/client";
import type { Analysis } from "@unruly-osdk/sdk";

export const AnalysisPage: React.FC = () => {
  const services = useServices();
  const navigate = useNavigate();
  const params = useParams<{ analysisId: string }>();
  const analysisId = params.analysisId;
  const { data: user } = services.User.useGetCurrent();
  const shareDialogRef = useRef<ShareDialogHandle>(null);
  const toastRef = useRef<SharedToLinkedInToastHandle>(null);

  const handleOpenAnalysis = (
    analysis: Osdk.Instance<Analysis>,
    _eventId: string,
  ) => {
    const route =
      analysis.type === "event deep dive"
        ? `/event-analysis/${analysis.id}`
        : analysis.type === "topic deep dive"
          ? `/topic-analysis/${analysis.id}`
          : "/";
    navigate(route);
  };

  const {
    data: selectedAnalysis,
    isLoading,
    isError,
  } = services.Analysis.useGetAnalysisById(analysisId || "");

  // Set document title based on analysis data
  const analysisTitle = isLoading
    ? "Loading Analysis..."
    : selectedAnalysis?.title || "Analysis";
  const analysisType =
    selectedAnalysis?.type === "event deep dive"
      ? "Event Deep-Dive"
      : selectedAnalysis?.type === "topic deep dive"
        ? "Topic Deep-Dive"
        : "Analysis";
  const createdAt = selectedAnalysis?.createdAt
    ? new Date(selectedAnalysis.createdAt).toLocaleDateString()
    : "";
  const pageSubtitle = isLoading ? "" : `${analysisType} · ${createdAt}`;
  useDocumentTitle(analysisTitle, pageSubtitle);

  const handleBack = () => {
    // Navigate back to the previous page or home
    navigate(-1);
  };

  if (isLoading) {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CircularProgress />
          <p>Loading analysis...</p>
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Alert severity="error">Failed to load analysis</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Go Back
          </Button>
        </div>
      </>
    );
  }

  if (!selectedAnalysis) {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Alert severity="warning">Analysis not found</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Go Back
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <AnalysisContent
        analysis={selectedAnalysis}
        isLoading={isLoading}
        userId={user?.id}
        onOpenAnalysis={handleOpenAnalysis}
        showShareDialog={(eventId) => shareDialogRef.current?.show(eventId)}
      />
      <ShareDialog
        onShareSuccess={(postId) => toastRef.current?.shareSuccess(postId)}
        ref={shareDialogRef}
      />
      <SharedToLinkedInToast ref={toastRef} />
    </>
  );
};

export default AnalysisPage;
